import React, { useMemo } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { FONT_FAMILY } from '../common/sharedFunctions';

const WA_GREEN = '#25d366';
const WA_DARK  = '#075e54';
const GOLD     = '#d4af37';
const WA_NUMBER = '33669675282';
const makeWALink = (text) => `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;

const FEATURES_FR = [
  {
    icon: <LocalTaxiIcon sx={{ fontSize: 30 }} />,
    title: 'Réserver un VTC à Toulouse, Montauban, Blagnac',
    desc: 'Chauffeur privé disponible 24/7 dans toute l\'agglomération toulousaine. Déplacements professionnels, aéroport, gare ou soirées. Prix fixe garanti.',
    prompt: 'Je veux réserver une course VTC à Toulouse ou Montauban'
  },
  {
    icon: <DeliveryDiningIcon sx={{ fontSize: 30 }} />,
    title: 'Livraison de repas à domicile - Toulouse · Montauban · Blagnac',
    desc: "Vos restaurants préférés livrés chez vous en 20-30 minutes. Sushi, burgers, pizzas, cuisine asiatique, libanaise, locale... Commandez via WhatsApp sans télécharger l'app. Service disponible à Toulouse, Montauban, Blagnac et environs.",
    prompt: 'Je veux commander un plat à emporter à Toulouse ou Montauban'
  },
  {
    icon: <PersonAddIcon sx={{ fontSize: 30 }} />,
    title: 'Devenir livreur ou restaurateur partenaire',
    desc: "Rejoignez notre réseau de livreurs à Toulouse, Montauban et Blagnac. Horaires flexibles, revenus attractifs. Ou inscrivez votre restaurant pour augmenter vos ventes avec notre service de livraison.",
    prompt: 'Je veux devenir livreur ou partenaire restaurant à Toulouse'
  }
];

const FEATURES_EN = [
  {
    icon: <LocalTaxiIcon sx={{ fontSize: 30 }} />,
    title: 'Book a VTC ride',
    desc: 'Tell Gaëlle where you\'re going. She estimates the fare, presents vehicle options and confirms your booking.',
    prompt: 'I want to book a VTC ride'
  },
  {
    icon: <DeliveryDiningIcon sx={{ fontSize: 30 }} />,
    title: 'Allô Eats delivery',
    desc: 'Order a meal or register as an Eats courier directly on WhatsApp, no app download required first.',
    prompt: 'I want to order or become an Eats courier'
  },
  {
    icon: <PersonAddIcon sx={{ fontSize: 30 }} />,
    title: 'Driver, courier or restaurant signup',
    desc: 'Gaëlle collects your info and documents, creates your account and follows up on missing files automatically.',
    prompt: 'I want to sign up as a driver or courier'
  }
];

const GaelleFeatureSection = React.memo(() => {
  const { i18n } = useTranslation();
  const lang = useMemo(() => {
    const raw = i18n?.language || i18n?.languages?.[0] || 'fr';
    return raw.toLowerCase().startsWith('fr') ? 'fr' : 'en';
  }, [i18n?.language, i18n?.languages]);

  const features = lang === 'fr' ? FEATURES_FR : FEATURES_EN;

  const openGaelle = (prompt) => {
    const link = makeWALink(prompt);
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 5, md: 8 }, background: 'linear-gradient(180deg, #010f0b 0%, #050505 100%)' }}>
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          {/* Badge */}
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: .75, borderRadius: '100px', backgroundColor: `${WA_GREEN}18`, border: `1px solid ${WA_GREEN}44`, mb: 2.5 }}>
            <AutoAwesomeIcon sx={{ fontSize: 14, color: WA_GREEN }} />
            <Typography sx={{ color: WA_GREEN, fontFamily: FONT_FAMILY, fontWeight: 900, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
              {lang === 'fr' ? 'Nouveau · Assistant IA WhatsApp' : 'New · WhatsApp AI assistant'}
            </Typography>
          </Box>

          <Typography component="h2" sx={{ color: '#fff', fontFamily: FONT_FAMILY, fontWeight: 900, fontSize: { xs: 30, md: 48 }, lineHeight: 1.06, letterSpacing: '-.5px', mb: 2 }}>
            {lang === 'fr'
              ? <>Gaëlle fait tout, <Box component="span" sx={{ color: WA_GREEN }}>sur WhatsApp</Box></>
              : <>Gaëlle does it all, <Box component="span" sx={{ color: WA_GREEN }}>on WhatsApp</Box></>
            }
          </Typography>

          <Typography sx={{ color: 'rgba(255,255,255,.65)', fontFamily: FONT_FAMILY, fontSize: { xs: 15, md: 18 }, lineHeight: 1.6, maxWidth: 680, mx: 'auto', mb: 3.5 }}>
            {lang === 'fr'
              ? 'Réservez une course VTC, commandez un plat à emporter ou inscrivez-vous comme chauffeur ou livreur, directement dans votre messagerie WhatsApp, sans compte, sans app.'
              : 'Book a VTC ride, order a takeaway or sign up as a driver or courier, right inside WhatsApp, no account or app required.'
            }
          </Typography>

          <Button
            component="a"
            href={makeWALink(lang === 'fr' ? 'Bonjour Gaëlle' : 'Hello Gaëlle')}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<WhatsAppIcon />}
            sx={{
              backgroundColor: WA_GREEN,
              color: '#050505',
              fontFamily: FONT_FAMILY,
              fontWeight: 900,
              fontSize: { xs: 15, md: 17 },
              borderRadius: '14px',
              px: 3.5,
              py: 1.4,
              textTransform: 'none',
              boxShadow: `0 0 28px ${WA_GREEN}55`,
              '&:hover': { backgroundColor: '#1db954' }
            }}
          >
            {lang === 'fr' ? 'Écrire à Gaëlle sur WhatsApp' : 'Message Gaëlle on WhatsApp'}
          </Button>

          <Typography sx={{ color: 'rgba(255,255,255,.32)', fontFamily: FONT_FAMILY, fontSize: 12, mt: 1.2 }}>
            +33 6 69 67 52 82
          </Typography>
        </Box>

        {/* Feature cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {features.map((f) => (
            <Grid item xs={12} md={4} key={f.title}>
              <Box
                onClick={() => openGaelle(f.prompt)}
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  minHeight: 210,
                  p: { xs: 2.5, md: 3 },
                  borderRadius: '20px',
                  backgroundColor: 'rgba(255,255,255,.045)',
                  border: `1px solid ${WA_GREEN}2a`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.8,
                  transition: 'border-color .2s, background-color .2s',
                  '&:hover': {
                    borderColor: `${WA_GREEN}66`,
                    backgroundColor: `${WA_GREEN}0a`
                  }
                }}
              >
                <Box sx={{ width: 52, height: 52, borderRadius: '14px', backgroundColor: `${WA_GREEN}1a`, border: `1px solid ${WA_GREEN}44`, display: 'grid', placeItems: 'center', color: WA_GREEN }}>
                  {f.icon}
                </Box>
                <Box>
                  <Typography sx={{ color: '#fff', fontFamily: FONT_FAMILY, fontWeight: 900, fontSize: { xs: 18, md: 20 }, mb: .7 }}>
                    {f.title}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,.60)', fontFamily: FONT_FAMILY, fontSize: 14, lineHeight: 1.55 }}>
                    {f.desc}
                  </Typography>
                </Box>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .6, color: WA_GREEN, mt: 'auto' }}>
                  <WhatsAppIcon sx={{ fontSize: 15 }} />
                  <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 900, fontSize: 13 }}>
                    {lang === 'fr' ? 'Demander à Gaëlle' : 'Ask Gaëlle'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Bottom strip */}
        <Box sx={{ p: { xs: 2, md: 2.5 }, borderRadius: '16px', background: `linear-gradient(90deg, ${WA_DARK}55 0%, ${WA_DARK}22 100%)`, border: `1px solid ${WA_GREEN}30`, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '12px', backgroundColor: WA_GREEN, display: 'grid', placeItems: 'center' }}>
              <WhatsAppIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ color: '#fff', fontFamily: FONT_FAMILY, fontWeight: 900, fontSize: { xs: 15, md: 17 } }}>
                {lang === 'fr' ? 'Disponible 24h/24 · Répond en quelques secondes' : 'Available 24/7 · Responds in seconds'}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,.5)', fontFamily: FONT_FAMILY, fontSize: 13 }}>
                {lang === 'fr' ? 'Aucune installation requise - WhatsApp uniquement.' : 'No installation needed - WhatsApp only.'}
              </Typography>
            </Box>
          </Box>
          <Button
            component="a"
            href={makeWALink(lang === 'fr' ? 'Bonjour Gaëlle' : 'Hello Gaëlle')}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<WhatsAppIcon />}
            sx={{ color: '#050505', backgroundColor: WA_GREEN, borderRadius: '12px', px: 2.5, py: 1.1, textTransform: 'none', fontFamily: FONT_FAMILY, fontWeight: 900, whiteSpace: 'nowrap', flexShrink: 0, '&:hover': { backgroundColor: '#1db954' } }}
          >
            {lang === 'fr' ? 'Démarrer maintenant' : 'Start now'}
          </Button>
        </Box>

      </Box>
    </Box>
  );
});

export default GaelleFeatureSection;
